import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { io } from 'socket.io-client';
import { API_BASE_URL } from '../../config';
import './embed.css';

/**
 * EmbedVideo - Shows videos based on party phase progression
 *
 * Usage: /embed/video
 *
 * URL Parameters:
 *   - muted: 'true' or 'false' (default: true)
 *   - autoplay: 'true' or 'false' (default: true)
 *   - loop: 'true' or 'false' (default: true) - loop through all unlocked videos
 *   - fit: 'cover', 'contain', or 'fill' (default: cover)
 *   - bg: background color (default: #000000)
 *   - mode: 'current' or 'playlist' (default: playlist)
 *     - current: only show video for current phase
 *     - playlist: cycle through all unlocked videos
 */

// Video files by phase key
const VIDEO_FILES = {
  'registration': '/videos/registration.mp4',
  'orientation': '/videos/orientation.mp4',
  'trading-1': '/videos/trading-1.mp4',
  'portfolio-warfare': '/videos/portfolio-warfare.mp4',
  'trading-2': '/videos/trading-2.mp4',
  'dead-cat-bounce': '/videos/dead-cat-bounce.mp4',
  'trading-3': '/videos/trading-3.mp4',
  'a-brief-case': '/videos/a-brief-case.mp4',
  'trading-4': '/videos/trading-4.mp4',
  'liquidity-crisis': '/videos/liquidity-crisis.mp4',
  'closing': '/videos/closing.mp4',
  'winners': '/videos/winners.mp4',
};

// Ordered progression of phases/videos
const PHASE_ORDER = [
  'registration',
  'orientation',
  'trading-1',
  'portfolio-warfare',
  'trading-2',
  'dead-cat-bounce',
  'trading-3',
  'a-brief-case',
  'trading-4',
  'liquidity-crisis',
  'closing',
  'winners',
];

// Map party state to phase key
const getPhaseKey = (phase, tradingBlock, macroGame) => {
  if (phase === 'not_started' || phase === 'registration' || phase === 'premarket') {
    return 'registration';
  }
  if (phase === 'orientation') {
    return 'orientation';
  }
  if (phase === 'trading') {
    return `trading-${tradingBlock}`;
  }
  if (phase === 'macro_game' && macroGame) {
    return macroGame.toLowerCase().replace(/\s+/g, '-');
  }
  if (phase === 'liquidity_crisis') {
    return 'liquidity-crisis';
  }
  if (phase === 'closing') {
    return 'closing';
  }
  if (phase === 'winners' || phase === 'results' || phase === 'ended') {
    return 'winners';
  }
  return 'registration';
};

// Get all unlocked videos up to current phase
const getUnlockedVideos = (currentPhaseKey) => {
  const currentIndex = PHASE_ORDER.indexOf(currentPhaseKey);
  if (currentIndex === -1) {
    return [{ key: 'registration', url: VIDEO_FILES['registration'] }];
  }

  const unlocked = [];
  for (let i = 0; i <= currentIndex; i++) {
    const key = PHASE_ORDER[i];
    if (VIDEO_FILES[key]) {
      unlocked.push({ key, url: VIDEO_FILES[key] });
    }
  }
  return unlocked;
};

const EmbedVideo = () => {
  const [searchParams] = useSearchParams();
  const videoRef = useRef(null);

  // URL params
  const muted = searchParams.get('muted') !== 'false';
  const autoplay = searchParams.get('autoplay') !== 'false';
  const loop = searchParams.get('loop') !== 'false';
  const fit = searchParams.get('fit') || 'cover';
  const bg = searchParams.get('bg') || '#000000';
  const mode = searchParams.get('mode') || 'playlist'; // 'current' or 'playlist'

  const [partyPhase, setPartyPhase] = useState('not_started');
  const [tradingBlock, setTradingBlock] = useState(1);
  const [currentMacroGame, setCurrentMacroGame] = useState(null);
  const [unlockedVideos, setUnlockedVideos] = useState([]);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [videoError, setVideoError] = useState(false);
  const socketRef = useRef(null);

  // Update unlocked videos when phase changes
  useEffect(() => {
    const phaseKey = getPhaseKey(partyPhase, tradingBlock, currentMacroGame);
    const videos = getUnlockedVideos(phaseKey);
    setUnlockedVideos(videos);

    // In playlist mode, keep cycling; in current mode, jump to latest
    if (mode === 'current') {
      setCurrentVideoIndex(videos.length - 1);
    }
  }, [partyPhase, tradingBlock, currentMacroGame, mode]);

  // Fetch initial state + poll every 10 seconds as backup to WebSocket
  useEffect(() => {
    const fetchState = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/party-state`);
        if (res.ok) {
          const data = await res.json();
          if (data.partyState) {
            setPartyPhase(data.partyState.phase || 'not_started');
            setTradingBlock(data.partyState.tradingBlockNumber || 1);
            setCurrentMacroGame(data.partyState.currentMacroGame || null);
          }
        }
      } catch (error) {
        console.error('Error fetching party state:', error);
      }
    };

    fetchState();
    const interval = setInterval(fetchState, 10000);
    return () => clearInterval(interval);
  }, []);

  // Socket connection for real-time updates
  useEffect(() => {
    socketRef.current = io(API_BASE_URL, {
      auth: { public: true }
    });

    socketRef.current.on('party-state-update', (data) => {
      if (data.partyState) {
        setPartyPhase(data.partyState.phase || 'not_started');
        setTradingBlock(data.partyState.tradingBlockNumber || 1);
        setCurrentMacroGame(data.partyState.currentMacroGame || null);
      }
    });

    socketRef.current.on('registration-start', () => {
      setPartyPhase('registration');
    });

    socketRef.current.on('premarket-start', () => {
      setPartyPhase('premarket');
    });

    socketRef.current.on('orientation-start', () => {
      setPartyPhase('orientation');
    });

    socketRef.current.on('market-bell', (data) => {
      if (data.type === 'OPENING_BELL') {
        setPartyPhase('trading');
      } else if (data.type === 'CLOSING_BELL') {
        setPartyPhase('closing');
      }
    });

    socketRef.current.on('trading-block-countdown-start', (data) => {
      setTradingBlock(data.blockNumber || 1);
      setPartyPhase('trading');
    });

    socketRef.current.on('macro-game-countdown', (data) => {
      setPartyPhase('macro_game');
      setCurrentMacroGame(data.gameName || null);
    });

    socketRef.current.on('macro-game-start', (data) => {
      setPartyPhase('macro_game');
      setCurrentMacroGame(data.gameName || null);
    });

    socketRef.current.on('party-restart', () => {
      setPartyPhase('not_started');
      setTradingBlock(1);
      setCurrentMacroGame(null);
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, []);

  // Handle video ended - advance to next in playlist
  const handleVideoEnded = () => {
    if (loop && unlockedVideos.length > 1) {
      setCurrentVideoIndex(prev => (prev + 1) % unlockedVideos.length);
    }
  };

  // Handle video error - skip to next or show error
  const handleVideoError = () => {
    const currentVideo = unlockedVideos[currentVideoIndex];
    console.warn(`Video not found: ${currentVideo?.url}`);

    // Try next video if available
    if (unlockedVideos.length > 1) {
      setCurrentVideoIndex(prev => (prev + 1) % unlockedVideos.length);
    } else {
      setVideoError(true);
    }
  };

  // Restart video when source changes
  useEffect(() => {
    if (videoRef.current && unlockedVideos[currentVideoIndex]) {
      videoRef.current.load();
      if (autoplay) {
        videoRef.current.play().catch(e => console.log('Autoplay prevented:', e));
      }
    }
  }, [currentVideoIndex, unlockedVideos, autoplay]);

  const currentVideo = unlockedVideos[currentVideoIndex];
  const phaseKey = getPhaseKey(partyPhase, tradingBlock, currentMacroGame);

  return (
    <div
      className="embed-video"
      style={{
        background: bg,
        width: '100vw',
        height: '100vh',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative'
      }}
    >
      <button className="embed-fullscreen-btn embed-fullscreen-corner" onClick={() => document.documentElement.requestFullscreen?.()}>&#x26F6;</button>
      {currentVideo && !videoError ? (
        <video
          ref={videoRef}
          src={currentVideo.url}
          muted={muted}
          autoPlay={autoplay}
          playsInline
          controls
          onEnded={handleVideoEnded}
          onError={handleVideoError}
          onDoubleClick={(e) => {
            if (document.fullscreenElement) {
              document.exitFullscreen();
            } else {
              e.target.requestFullscreen?.();
            }
          }}
          style={{
            width: '100%',
            height: '100%',
            objectFit: fit
          }}
        />
      ) : (
        <div style={{
          color: '#666',
          fontSize: '14px',
          textAlign: 'center',
          padding: '20px'
        }}>
          <div style={{ marginBottom: '10px' }}>
            No video available for phase: {phaseKey}
          </div>
          <div style={{ fontSize: '12px', opacity: 0.7 }}>
            Unlocked: {unlockedVideos.map(v => v.key).join(', ')}
          </div>
        </div>
      )}

      {/* Debug info - remove in production */}
      {searchParams.get('debug') === 'true' && (
        <div style={{
          position: 'absolute',
          bottom: '10px',
          left: '10px',
          background: 'rgba(0,0,0,0.8)',
          color: '#fff',
          padding: '10px',
          fontSize: '12px',
          borderRadius: '4px',
          maxWidth: '300px'
        }}>
          <div>Phase: {partyPhase}</div>
          <div>Block: {tradingBlock}</div>
          <div>Game: {currentMacroGame || 'none'}</div>
          <div>Key: {phaseKey}</div>
          <div>Videos: {unlockedVideos.length}</div>
          <div>Current: {currentVideoIndex + 1}/{unlockedVideos.length} - {currentVideo?.key}</div>
        </div>
      )}
    </div>
  );
};

export default EmbedVideo;
